<script>
import { VTextField, VIcon } from 'vuetify/lib'
import ELabel from '../../ELabel/ELabel.vue'
import mergeData from 'vuetify/lib/util/mergeData'
import { kebabCase } from 'vuetify/lib/util/helpers'
import ECounter from './ECounter.vue'
import sizeable from '../../../mixins/sizeable'

export default {
  name: 'ETextField',
  extends: VTextField,
  mixins: [sizeable],
  props: {
    required: {
      type: Boolean,
      default: false,
    },
    dense: {
      type: Boolean,
      default: true,
    },
    outlined: {
      type: Boolean,
      default: true,
    },
    hideDetails: {
      type: [Boolean, String],
      default: 'auto',
    },
  },
  computed: {
    classes() {
      return {
        ...VTextField.options.computed.classes.call(this),
        'e-text-field': true,
        'e-text-field--success': this.validationState === 'success',
        'e-text-field--error': this.validationState === 'error',
        ...this.sizeableClasses,
      }
    },
    labelValue() {
      return true
    },
  },
  methods: {
    genLabel() {
      if (!this.showLabel) return null
      const data = {
        props: {
          required: this.required,
        },
      }
      return this.$createElement(ELabel, data, this.$slots.label || this.label)
    },
    genTextFieldSlot() {
      return this.$createElement(
        'div',
        {
          staticClass: 'v-text-field__slot',
        },
        [this.prefix ? this.genAffix('prefix') : null, this.genInput(), this.suffix ? this.genAffix('suffix') : null]
      )
    },
    genInput() {
      const listeners = Object.assign({}, this.listeners$)
      delete listeners.change // Change should not be bound externally

      const { title, ...inputAttrs } = this.attrs$
      return this.$createElement('input', {
        style: {},
        domProps: {
          value: this.type === 'number' && Object.is(this.lazyValue, -0) ? '-0' : this.lazyValue,
        },
        attrs: {
          ...inputAttrs,
          autofocus: this.autofocus,
          disabled: this.isDisabled,
          id: this.computedId,
          placeholder: this.placeholder,
          readonly: this.isReadonly,
          type: this.type,
        },
        on: Object.assign(listeners, {
          blur: this.onBlur,
          input: this.onInput,
          focus: this.onFocus,
          keydown: this.onKeyDown,
        }),
        ref: 'input',
        directives: [
          {
            name: 'resize',
            modifiers: {
              quiet: true,
            },
            value: this.onResize,
          },
        ],
      })
    },
    genAppendSlot() {
      const slot = []

      if (this.$slots['append-outer']) {
        slot.push(this.$slots['append-outer'])
      } else if (this.appendOuterIcon) {
        slot.push(this.genIcon('appendOuter'))
      }

      return this.genSlot('append', 'outer', slot)
    },

    genPrependInnerSlot() {
      const slot = []

      if (this.$slots['prepend-inner']) {
        slot.push(this.$slots['prepend-inner'])
      } else if (this.prependInnerIcon) {
        slot.push(this.genIcon('prependInner'))
      }

      return this.genSlot('prepend', 'inner', slot)
    },
    genIcon(type, cb, extraData = {}) {
      const icon = this[`${type}Icon`]
      const eventName = `click:${kebabCase(type)}`
      const hasListener = !!(this.listeners$[eventName] || cb)
      const data = mergeData(
        {
          attrs: {
            'aria-label': hasListener ? kebabCase(type).split('-')[0] + ' icon' : undefined,
            dark: this.dark,
            disabled: this.isDisabled,
            light: this.light,
          },
          on: !hasListener
            ? undefined
            : {
                click: (e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  this.$emit(eventName, e)
                  cb && cb(e)
                },
                // Container has g event that will
                // trigger menu open if enclosed
                mouseup: (e) => {
                  e.preventDefault()
                  e.stopPropagation()
                },
              },
        },
        extraData
      )
      return this.$createElement(
        'div',
        {
          staticClass: `v-input__icon`,
          class: type ? `v-input__icon--${kebabCase(type)}` : undefined,
        },
        [this.$createElement(VIcon, data, icon)]
      )
    },
    genIconSlot() {
      const slot = []

      if (this.$slots.append) {
        slot.push(this.$slots.append)
      } else if (this.appendIcon) {
        slot.push(this.genIcon('append'))
      } else if (this.validationState === 'error') {
        slot.push(
          this.$createElement(
            'div',
            {
              staticClass: `v-input__icon`,
              class: 'v-input__icon--append v-input__icon--append-error',
            },
            [this.$createElement(VIcon, 'icon-exclamation')]
          )
        )
      } else if (this.validationState === 'success') {
        slot.push(
          this.$createElement(
            'div',
            {
              staticClass: `v-input__icon`,
              class: 'v-input__icon--append v-input__icon--append-success',
            },
            [this.$createElement(VIcon, 'icon-success')]
          )
        )
      }

      return this.genSlot('append', 'inner', slot)
    },
    genCounter() {
      var _this$$scopedSlots$co, _this$$scopedSlots$co2, _this$$scopedSlots

      if (!this.hasCounter) return null
      const max = this.counter === true ? this.attrs$.maxlength : this.counter
      const props = {
        dark: this.dark,
        light: this.light,
        max,
        value: this.computedCounterValue,
      }
      return (_this$$scopedSlots$co =
        (_this$$scopedSlots$co2 = (_this$$scopedSlots = this.$scopedSlots).counter) == null
          ? void 0
          : _this$$scopedSlots$co2.call(_this$$scopedSlots, {
              props,
            })) != null
        ? _this$$scopedSlots$co
        : this.$createElement(ECounter, {
            props,
          })
    },
    genFieldset() {
      if (!this.outlined) return null
      return this.$createElement(
        'fieldset',
        {
          attrs: {
            'aria-hidden': true,
          },
          style: {
            top: 0,
            bottom: 0,
            borderWidth: '1px',
          },
        },
        [this.genLabel()]
      )
    },
  },
}
</script>
